import React from "react";
import { fmtMins } from "../../components/classic/micro";
import { useCount, useSpot } from "./WowMotion";
import { Gauge, WeekStream, HourColumns, hourPeople, isSingleDay, prettyDay, inks, paper, SkelBox, SkelBars } from "./viz";

/**
 * HeroBand — tracked hours, active members, gauge, week stream + columns.
 * Hours + members prefer livePeople (team-hours — same source as V2).
 */
export function HeroBand({ t, overview, loading, onDay, footer, livePeople, liveLoading }) {
  const tot = overview?.totals;
  const people = overview?.people;
  const series = overview?.daySeries || [];
  const single = isSingleDay(overview);
  const hoursOfDay = hourPeople(overview);
  const spot = useSpot();
  const k = inks(t);
  const ppr = paper(t);

  const liveReady = !liveLoading && livePeople && livePeople.teamSize != null;
  const tracked = liveReady && livePeople.trackedMinutes != null
    ? livePeople.trackedMinutes
    : (tot?.trackedMinutes ?? 0);
  const active = liveReady && livePeople.activeMinutes != null
    ? livePeople.activeMinutes
    : (tot?.activeMinutes ?? 0);
  const idle = liveReady && livePeople.idleMinutes != null
    ? livePeople.idleMinutes
    : (tot?.idleMinutes ?? 0);
  const prev = liveReady && livePeople.prevTrackedMinutes != null
    ? livePeople.prevTrackedMinutes
    : (tot?.prevTrackedMinutes ?? 0);
  const delta = prev > 0 ? Math.round(((tracked - prev) / prev) * 100) : null;
  const activePct = tracked > 0 ? Math.round((active / tracked) * 100) : 0;
  const trackedN = liveReady ? (livePeople.activeMembers ?? 0) : (liveLoading ? 0 : (people?.tracked ?? 0));
  const totalN = liveReady ? (livePeople.teamSize ?? 0) : (liveLoading ? 0 : (people?.total ?? 0));
  const silentN = Math.max(0, totalN - trackedN);
  const memberPct = totalN > 0 ? Math.round((trackedN / totalN) * 100) : 0;

  // Count the MINUTES, not whole hours. This used to be
  // `useCount(Math.round(tracked / 60))` rendered as `${hours}h`, which both hid
  // the minutes and could overstate — 12h31m showed as "13h", 45m showed as "1h".
  const trackedMins = useCount(Math.round(tracked), !loading && tracked > 0);
  const trackedH = Math.floor(trackedMins / 60);
  const trackedM = String(trackedMins % 60).padStart(2, "0");
  const members = useCount(trackedN, !loading && !liveLoading && totalN > 0);

  const sh = t.shadow.raised;

  return (
    <div
      className="tgw-stage tgw-spot tgw-a tgw-a1"
      onMouseMove={spot}
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(260px, 320px) 1fr",
        borderRadius: t.r.hero,
        background: ppr.card,
        boxShadow: sh,
        border: `1px solid ${t.c.border}`,
        marginBottom: 16,
      }}
    >
      <div style={{
        padding: "28px 26px 22px",
        background: ppr.well,
        borderRight: `1px solid ${t.c.border}`,
      }}>
        <div style={{ ...t.font.statLabel, color: k.blue }}>
          Tracked
        </div>
        <div style={{
          fontSize: 72, fontWeight: 680, letterSpacing: "-3.4px", lineHeight: 1,
          margin: "10px 0 10px", fontVariantNumeric: "tabular-nums",
        }}>
          {loading ? <SkelBox t={t} w={160} h={56} r={12} /> : (
            /* Minutes ride along at a smaller size so the hero number stays the
               hours, but the value is never rounded away. */
            trackedH > 0 ? (
              <>
                {trackedH}h
                <span style={{ fontSize: 34, fontWeight: 640, letterSpacing: "-1.2px", marginLeft: 8 }}>
                  {trackedM}m
                </span>
              </>
            ) : <>{trackedMins}m</>
          )}
        </div>
        {delta != null && (
          <span style={{
            display: "inline-flex", marginTop: 4, padding: "4px 10px", borderRadius: 99,
            background: t.c.primarySoft, color: k.blueD, fontSize: 12, fontWeight: 750,
          }}>
            {delta >= 0 ? "↑" : "↓"} {Math.abs(delta)}% vs last period
          </span>
        )}

        {totalN > 0 && (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "16px 0 10px", paddingTop: 2 }}>
              <div style={{ fontSize: 28, fontWeight: 720, letterSpacing: "-1.2px", lineHeight: 1.2, fontVariantNumeric: "tabular-nums" }}>
                {loading ? "—" : members}
                <i style={{ fontStyle: "normal", color: k.dim, fontSize: 18, fontWeight: 650 }}> / {totalN}</i>
              </div>
              <span style={{ fontSize: 13, fontWeight: 650, color: t.c.textSecondary, lineHeight: 1.2 }}>active members</span>
            </div>
            <div
              title={`${trackedN} of ${totalN} members active this window (live)`}
              style={{ height: 8, borderRadius: 99, background: t.c.border, overflow: "hidden", margin: "0 0 6px" }}
            >
              <i className="tgw-growX" style={{
                display: "block", height: "100%", width: `${memberPct}%`, borderRadius: 99,
                background: k.blue,
              }} />
            </div>
            <div style={{ fontSize: 12, color: k.dim }}>
              <b style={{ color: t.c.text }}>{trackedN}</b> tracked · <b style={{ color: t.c.text }}>{silentN}</b> silent of {totalN}
            </div>
          </>
        )}

        {!loading && tracked > 0 && <Gauge t={t} activePct={activePct} />}

        {!loading && tracked > 0 && (
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 8, fontSize: 12.5, color: t.c.textSecondary }}>
            <span><i style={{ width: 8, height: 8, borderRadius: 2, display: "inline-block", marginRight: 5, background: k.blue }} /><b style={{ color: t.c.text }}>{fmtMins(active)}</b> active</span>
            <span><i style={{ width: 8, height: 8, borderRadius: 2, display: "inline-block", marginRight: 5, background: k.gold }} /><b style={{ color: t.c.text }}>{fmtMins(idle)}</b> idle</span>
          </div>
        )}
      </div>

      <div style={{ padding: "16px 18px 12px", background: ppr.card, minWidth: 0, display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", padding: "4px 6px 0" }}>
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, letterSpacing: "-0.2px" }}>
            {single ? "Hours through the day" : "Hours by day"}
          </h3>
          <span style={{ fontSize: 11.5, color: k.dim, fontWeight: 600 }}>
            {single
              ? (overview?.range?.start ? prettyDay(overview.range.start) : "one hour per column")
              : "click a day to open it"}
          </span>
        </div>
        {loading ? <SkelBars t={t} height={230} /> : single && hoursOfDay ? (
          <HourColumns t={t} hourPeople={hoursOfDay} />
        ) : series.length ? (
          <WeekStream t={t} series={series} onDay={onDay} />
        ) : (
          <div style={{ padding: 40, textAlign: "center", color: k.dim, fontSize: 13 }}>Nothing tracked in this window.</div>
        )}
      </div>

      {/* The numbers above, their written version one click below — an
          optional band fused to the hero's bottom edge, spanning both
          columns. The dashboard passes it only for org admins. */}
      {footer ? (
        <div style={{ gridColumn: "1 / -1", borderTop: `1px solid ${t.c.border}` }}>
          {footer}
        </div>
      ) : null}
    </div>
  );
}

export default HeroBand;
